//------------------------------------------------------ externos
import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Select from "../components/inputs/SelectBase";
//------------------------------------------------------ funciones
import { useAuth } from "../auth/AuthContext";
import { useRealtime } from "../services/store";
import { getCargasDeViaje, createCarga } from "../services/cols/cargasCombustible.service";
import { finalizarViaje, deleteViaje, reintentarScania } from "../services/cols/viajes.service";
import { formatearFecha } from "../functions/dataFunctions";
import { enAR } from "../shared/fechaAr";
import Patente from "../components/funcionales/Patente";
//------------------------------------------------------ estilos
import "./css/Sections.css";
import "./css/Viajes.css";

// único usuario habilitado para eliminar viajes (coincide con la policy
// RESTRICTIVE de Supabase sobre la tabla `viajes` — si esto cambia, hay
// que actualizar también la policy en la base)
const UID_PUEDE_ELIMINAR = "e236671f-71ae-4f44-8ca7-d7bde0883017";

const ahoraDatetimeLocal = () => {
  // El «ahora» del input es el argentino, no el de la máquina (fechaAr.js).
  return enAR(new Date()).toISOString().slice(0, 16);
};

const numero = (n, decimales = 1) =>
  n === null || n === undefined || n === "" || Number.isNaN(Number(n))
    ? "—"
    : Number(n).toLocaleString("es-AR", {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      });

const ViajeDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeEliminar = user?.id === UID_PUEDE_ELIMINAR;

  const viajes = useRealtime("viajes");
  const cargasTodas = useRealtime("cargasCombustible");
  const tractores = useRealtime("tractores");

  const viaje = viajes.find((v) => String(v.id) === String(id));
  const cargas = useMemo(
    () => getCargasDeViaje(cargasTodas, id),
    [cargasTodas, id],
  );

  const opcionesTractores = tractores.map((tr) => ({
    value: String(tr.id),
    label: tr.label || String(tr.id),
  }));

  const internoDefault = cargas.length
    ? cargas[cargas.length - 1].interno
    : null;

  // ---------- form: agregar carga ----------
  const [internoCarga, setInternoCarga] = useState(null);
  const [fechaCarga, setFechaCarga] = useState(ahoraDatetimeLocal());
  const [litrosCarga, setLitrosCarga] = useState("");
  const [kmCarga, setKmCarga] = useState("");
  const [guardandoCarga, setGuardandoCarga] = useState(false);
  const [errorCarga, setErrorCarga] = useState("");

  // ---------- form: finalizar viaje ----------
  const [mostrarFinalizar, setMostrarFinalizar] = useState(false);
  const [internoFinal, setInternoFinal] = useState(null);
  const [fechaFin, setFechaFin] = useState(ahoraDatetimeLocal());
  const [kmFinal, setKmFinal] = useState("");
  const [litrosFinal, setLitrosFinal] = useState("");
  const [finalizando, setFinalizando] = useState(false);
  const [errorFinalizar, setErrorFinalizar] = useState("");

  // ---------- finalizar con una sola carga (viaje sin tramos) ----------
  const [confirmandoUnicoTramo, setConfirmandoUnicoTramo] = useState(false);

  // ---------- eliminar viaje ----------
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState("");

  // ---------- reintentar SCANIA (tramos que quedaron sin datos) ----------
  const [reintentando, setReintentando] = useState(false);
  const [errorReintentar, setErrorReintentar] = useState("");

  if (!viaje) {
    return (
      <section className="section">
        <div className="section-container">
          <p>Viaje no encontrado.</p>
          <Link to="/viajes" className="btn btn--secondary">
            ← Volver a Viajes
          </Link>
        </div>
      </section>
    );
  }

  const internoCargaValue =
    internoCarga ||
    (internoDefault
      ? opcionesTractores.find((o) => o.value === String(internoDefault))
      : null);

  const handleAgregarCarga = async (e) => {
    e.preventDefault();
    const interno = internoCargaValue?.value;
    if (!interno || !fechaCarga || litrosCarga === "" || kmCarga === "") {
      setErrorCarga("Completá interno, fecha, litros y kilómetros.");
      return;
    }
    setErrorCarga("");
    setGuardandoCarga(true);
    try {
      await createCarga({
        viaje: viaje.id,
        interno,
        fecha: fechaCarga,
        litros: litrosCarga,
        kilometros: kmCarga,
      });
      setInternoCarga(null);
      setFechaCarga(ahoraDatetimeLocal());
      setLitrosCarga("");
      setKmCarga("");
    } catch (err) {
      setErrorCarga(err.message || "No se pudo guardar la carga.");
    } finally {
      setGuardandoCarga(false);
    }
  };

  const internoFinalValue =
    internoFinal ||
    (internoDefault
      ? opcionesTractores.find((o) => o.value === String(internoDefault))
      : null);

  const handleFinalizar = async (e) => {
    e.preventDefault();
    const interno = internoFinalValue?.value;
    if (!interno || !fechaFin || kmFinal === "") {
      setErrorFinalizar("Completá interno final, fecha de fin y km del último tramo.");
      return;
    }
    setErrorFinalizar("");
    setFinalizando(true);
    try {
      await finalizarViaje({
        viaje,
        cargas,
        internoFinal: interno,
        fechaFin,
        kmFinal,
        litrosFinal: litrosFinal || 0,
      });
    } catch (err) {
      setErrorFinalizar(err.message || "No se pudo finalizar el viaje.");
    } finally {
      setFinalizando(false);
    }
  };

  const finalizado = viaje.estado === "finalizado";
  const tramos = finalizado ? viaje.tramosScania || [] : [];

  // si hay una sola carga cargada, ofrecemos el camino corto: confirmar
  // que el viaje es de un solo tramo (inicio -> esa carga) en vez de
  // pedir de nuevo todos los datos de cierre por un segundo tramo que
  // no aporta nada.
  const handleClickFinalizar = () => {
    if (cargas.length === 1) {
      setConfirmandoUnicoTramo(true);
    } else {
      setMostrarFinalizar(true);
    }
  };

  const handleFinalizarUnicoTramo = async () => {
    const unicaCarga = cargas[0];
    setErrorFinalizar("");
    setFinalizando(true);
    try {
      await finalizarViaje({
        viaje,
        cargas,
        internoFinal: unicaCarga.interno,
        fechaFin: unicaCarga.fecha,
        kmFinal: unicaCarga.kilometros,
        litrosFinal: 0,
      });
    } catch (err) {
      setErrorFinalizar(err.message || "No se pudo finalizar el viaje.");
    } finally {
      setFinalizando(false);
      setConfirmandoUnicoTramo(false);
    }
  };

  const handleEliminar = async () => {
    setErrorEliminar("");
    setEliminando(true);
    try {
      await deleteViaje(viaje.id);
      navigate("/viajes");
    } catch (err) {
      setErrorEliminar(err.message || "No se pudo eliminar el viaje.");
      setEliminando(false);
    }
  };

  const handleReintentarScania = async () => {
    setErrorReintentar("");
    setReintentando(true);
    try {
      await reintentarScania(viaje);
    } catch (err) {
      setErrorReintentar(err.message || "No se pudo reconsultar SCANIA.");
    } finally {
      setReintentando(false);
    }
  };

  return (
    <section className="section">
      <div className="section-container">
        <Link to="/viajes" className="link-button">
          ← Volver a Viajes
        </Link>

        <div className="viaje-header">
          <h1 className="section-title">
            Viaje <span className="plate-chip plate-chip--accent">{viaje.id}</span>
          </h1>
          <span
            className={`pill ${finalizado ? "ok" : "info"}`}
          >
            {finalizado ? "Finalizado" : "En curso"}
          </span>

          {puedeEliminar && (
            <div className="viaje-header__eliminar">
              {!confirmandoEliminar ? (
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => setConfirmandoEliminar(true)}
                >
                  Eliminar viaje
                </button>
              ) : (
                <div className="viajes-form__cliente-row">
                  <span className="field-label">¿Seguro? No se puede deshacer.</span>
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={handleEliminar}
                    disabled={eliminando}
                  >
                    {eliminando ? "Eliminando..." : "Sí, eliminar"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setConfirmandoEliminar(false)}
                    disabled={eliminando}
                  >
                    Cancelar
                  </button>
                </div>
              )}
              {errorEliminar && <p className="combustible-error">{errorEliminar}</p>}
            </div>
          )}
        </div>

        <div className="viaje-resumen">
          <div>
            <span className="field-label">Chofer</span>
            <p>{viaje.nombreChofer || "—"}</p>
          </div>
          <div>
            <span className="field-label">Cliente</span>
            <p>{viaje.nombreCliente || "—"}</p>
          </div>
          <div>
            <span className="field-label">Furgón</span>
            <p>
              {viaje.dominioFurgon ? <Patente dominio={viaje.dominioFurgon} /> : "—"}
            </p>
          </div>
          <div>
            <span className="field-label">Inicio</span>
            <p>{formatearFecha(viaje.fechaInicio)}</p>
          </div>
          {viaje.detalle && (
            <div>
              <span className="field-label">Detalle</span>
              <p>{viaje.detalle}</p>
            </div>
          )}
        </div>

        {/* ---------- cargas ---------- */}
        <h2 className="section-title">Cargas de combustible</h2>

        <div className="table-scroll-wrapper">
          <table className="table-lista">
            <thead className="table-titles">
              <tr>
                <th>Interno</th>
                <th>Fecha</th>
                <th>Litros</th>
                <th>Kilómetros</th>
              </tr>
            </thead>
            <tbody>
              {cargas.length === 0 && (
                <tr>
                  <td colSpan={4}>Todavía no hay cargas registradas.</td>
                </tr>
              )}
              {cargas.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className="plate-chip">{c.interno}</span>
                  </td>
                  <td>{formatearFecha(c.fecha)}</td>
                  <td>{numero(c.litros)} L</td>
                  <td>{numero(c.kilometros, 0)} km</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!finalizado && (
          <form className="viajes-form viajes-form--carga" onSubmit={handleAgregarCarga}>
            <div className="viajes-form__field">
              <label className="field-label">Interno</label>
              <Select
                options={opcionesTractores}
                value={internoCargaValue}
                onChange={setInternoCarga}
                placeholder="Interno..."
                noOptionsMessage={() => "Sin tractores"}
              />
            </div>
            <div className="viajes-form__field">
              <label className="field-label">Fecha</label>
              <input
                type="datetime-local"
                value={fechaCarga}
                onChange={(e) => setFechaCarga(e.target.value)}
              />
            </div>
            <div className="viajes-form__field">
              <label className="field-label">Litros</label>
              <input
                type="number"
                step="0.1"
                value={litrosCarga}
                onChange={(e) => setLitrosCarga(e.target.value)}
              />
            </div>
            <div className="viajes-form__field">
              <label className="field-label">Kilómetros</label>
              <input
                type="number"
                step="0.1"
                value={kmCarga}
                onChange={(e) => setKmCarga(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn--secondary" disabled={guardandoCarga}>
              {guardandoCarga ? "Guardando..." : "+ Agregar carga"}
            </button>
          </form>
        )}
        {errorCarga && <p className="combustible-error">{errorCarga}</p>}

        {/* ---------- finalizar / comparacion ---------- */}
        {!finalizado ? (
          <>
            <h2 className="section-title">Finalizar viaje</h2>
            {confirmandoUnicoTramo ? (
              <div className="viaje-resumen">
                <p>
                  ¿Estás seguro? Tu viaje comienza el{" "}
                  <strong>{formatearFecha(viaje.fechaInicio)}</strong> y
                  termina el{" "}
                  <strong>{formatearFecha(cargas[0].fecha)}</strong>.
                  Se va a guardar como un viaje de un solo tramo.
                </p>
                <div className="viajes-form__cliente-row">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleFinalizarUnicoTramo}
                    disabled={finalizando}
                  >
                    {finalizando ? "Consultando SCANIA..." : "Confirmar"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setConfirmandoUnicoTramo(false)}
                    disabled={finalizando}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : !mostrarFinalizar ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleClickFinalizar}
              >
                Finalizar viaje
              </button>
            ) : (
              <form className="viajes-form" onSubmit={handleFinalizar}>
                <div className="viajes-form__field">
                  <label className="field-label">Interno (último tramo)</label>
                  <Select
                    options={opcionesTractores}
                    value={internoFinalValue}
                    onChange={setInternoFinal}
                    placeholder="Interno..."
                    noOptionsMessage={() => "Sin tractores"}
                  />
                </div>
                <div className="viajes-form__field">
                  <label className="field-label">Fecha de fin</label>
                  <input
                    type="datetime-local"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>
                <div className="viajes-form__field">
                  <label className="field-label">Km del último tramo</label>
                  <input
                    type="number"
                    step="0.1"
                    value={kmFinal}
                    onChange={(e) => setKmFinal(e.target.value)}
                  />
                </div>
                <div className="viajes-form__field">
                  <label className="field-label">Litros cargados al llegar (opcional)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={litrosFinal}
                    onChange={(e) => setLitrosFinal(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn--primary" disabled={finalizando}>
                  {finalizando ? "Consultando SCANIA..." : "Confirmar y finalizar"}
                </button>
              </form>
            )}
            {errorFinalizar && <p className="combustible-error">{errorFinalizar}</p>}
          </>
        ) : (
          <>
            <div className="viaje-header">
              <h2 className="section-title">Declarado vs. SCANIA — por tramo</h2>
              {tramos.some((t) => t.error || t.kmScania == null) && (
                <button
                  type="button"
                  className="btn btn--secondary"
                  style={{ marginLeft: "auto" }}
                  onClick={handleReintentarScania}
                  disabled={reintentando}
                >
                  {reintentando ? "Reconsultando..." : "Reintentar SCANIA"}
                </button>
              )}
            </div>
            {errorReintentar && <p className="combustible-error">{errorReintentar}</p>}
            <div className="table-scroll-wrapper">
              <table className="table-lista viaje-comparativa__table">
                <thead className="table-titles">
                  <tr>
                    <th>Tramo</th>
                    <th>Interno</th>
                    <th>Desde</th>
                    <th>Hasta</th>
                    <th>Km decl.</th>
                    <th>Km SCANIA</th>
                    <th>Dif. km</th>
                    <th>L decl.</th>
                    <th>L SCANIA</th>
                    <th>Dif. L</th>
                    <th>Prom. decl.</th>
                    <th>Prom. SCANIA</th>
                  </tr>
                </thead>
                <tbody>
                  {tramos.map((t) => (
                    <tr key={t.tramo}>
                      <td>
                        {t.tramo}
                        {t.mismoDiaQueOtroTramo && (
                          <span title="SCANIA no distingue horario dentro del mismo día — este valor es del día completo">
                            {" "}
                            ⚠️
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="plate-chip">{t.interno}</span>
                      </td>
                      <td>{formatearFecha(t.desde)}</td>
                      <td>{formatearFecha(t.hasta)}</td>
                      <td>{numero(t.kmDeclarado, 0)}</td>
                      <td title={t.error ? `SCANIA: ${t.error}` : undefined}>
                        {numero(t.kmScania, 0)}
                      </td>
                      {/* la diferencia solo se evalúa contra el total del viaje
                          (más abajo) — por tramo, ventanas cortas o con error
                          de SCANIA daban diferencias que no significaban nada */}
                      <td className="txt-muted">—</td>
                      <td>{numero(t.litrosDeclarado)}</td>
                      <td title={t.error ? `SCANIA: ${t.error}` : undefined}>
                        {numero(t.litrosScania)}
                      </td>
                      <td className="txt-muted">—</td>
                      <td>{numero(t.promedioDeclarado)}</td>
                      <td>{numero(t.promedioScania)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="viaje-comparativa__total">
                    <td colSpan={4}>Total del viaje</td>
                    <td>{numero(viaje.kmDeclarado, 0)}</td>
                    <td>{numero(viaje.kmScania, 0)}</td>
                    <td
                      className={
                        viaje.kmScania - viaje.kmDeclarado < 0 ? "txt-neg" : "txt-pos"
                      }
                    >
                      {numero((viaje.kmScania || 0) - (viaje.kmDeclarado || 0), 0)}
                    </td>
                    <td>{numero(viaje.litrosDeclaradoTotal)}</td>
                    <td>{numero(viaje.litrosScania)}</td>
                    <td
                      className={
                        viaje.litrosScania - viaje.litrosDeclaradoTotal < 0
                          ? "txt-neg"
                          : "txt-pos"
                      }
                    >
                      {numero((viaje.litrosScania || 0) - (viaje.litrosDeclaradoTotal || 0))}
                    </td>
                    <td>
                      {numero(
                        viaje.kmDeclarado
                          ? (viaje.litrosDeclaradoTotal / viaje.kmDeclarado) * 100
                          : null,
                      )}
                    </td>
                    <td>{numero(viaje.promedioScania)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {tramos.some((t) => t.dataSource === "simulado") && (
              <p className="combustible-error">
                ⚠️ Algunos tramos muestran datos simulados — todavía no está SCANIA
                conectado para esas fechas/interno.
              </p>
            )}
            {tramos.some((t) => t.error) &&
              tramos.some((t) => !t.error && t.kmScania != null) && (
                <p className="txt-muted" style={{ fontSize: "var(--text-sm)" }}>
                  El total de SCANIA es parcial: no hay datos para todos los
                  tramos (pasá el mouse sobre el "—" de un tramo para el
                  detalle).
                </p>
              )}
          </>
        )}
      </div>
    </section>
  );
};

export default ViajeDetalle;
